import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

import { TokenStorageService } from './token-storage.service';
import { environment } from '../../../../environments/environment';
import { BehaviorSubject, Observable, catchError, filter, map, of, switchMap, take, throwError, firstValueFrom } from 'rxjs';
import { RedirectService } from './redirect.service';

type LoginRequest = {
  email: string;
  password: string;
};

type LoginResponse = {
  data: {
    jwt_token_id: number;
    account_id: number;
    account_first_name: string;
    account_last_name: string;
    jwt_token_access: string;
    jwt_token_refresh: string;
    jwt_token_creation: string;
    jwt_token_is_valid: boolean;
    jwt_refresh_token_expiration: string;
    account_role: string;
  };
  isSuccess: boolean;
  message: string;
};

@Injectable({
  providedIn: 'root',
})

export class AuthService {
  private http = inject(HttpClient);
  private tokenStorage = inject(TokenStorageService);
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);
  private isRefreshing = false;
  private redirectService = inject(RedirectService);


  async login(credentials: LoginRequest): Promise<void> {
    try {
      const response = await firstValueFrom(this.http
        .post<LoginResponse>(`${environment.apiUrl}/auth/login`, {
          email: credentials.email,
          password: credentials.password,
          twoFactorCode: null,
          twoFactorRecoveryCode: null,
        })
        .pipe(
          catchError((error: any) => {
            // Gestisci errori HTTP (500, 400, etc.)
            let errorMessage = 'Errore durante il login';
            
            if (error.error && error.error.message) {
              errorMessage = error.error.message;
            } else if (error.message) {
              errorMessage = error.message;
            }
            
            return throwError(() => new Error(errorMessage));
          })
        )
      );

      // Controlla se la risposta indica un errore anche se lo status è 200
      if (!response.isSuccess) {
        throw new Error(response.message || 'Login fallito');
      }

      this.tokenStorage.saveAccessToken(response.data.jwt_token_access);
      this.tokenStorage.saveRefreshToken(response.data.jwt_token_refresh);
      
      // Salva i dati dell'account
      if (response.data.account_id) {
        this.tokenStorage.saveAccountId(response.data.account_id);
      }
      if (response.data.account_role) {
        this.tokenStorage.saveRole(response.data.account_role);
      }
      if (response.data.account_first_name) {
        this.tokenStorage.saveAccountFirstName(response.data.account_first_name);
      }
      if (response.data.account_last_name) {
        this.tokenStorage.saveAccountLastName(response.data.account_last_name);
      }
      
      // Estrai e salva tutti i claims dal token (incluso userData)
      const isAccountEnabled = this.tokenStorage.extractAndSaveClaimsFromToken();
      
      // Se l'account è disabilitato, fai logout
      if (!isAccountEnabled) {
        this.logout();
        throw new Error('Account is disabled');
      }
    } catch (error: any) {
      // Rilancia l'errore con il messaggio corretto
      throw error;
    }
  }

  validateToken(): Observable<any> {
    return this.http.post(`${environment.apiUrl}/auth/validate-token`, {});
  }



  logout(): void {
    this.tokenStorage.clear();
    this.refreshTokenSubject.next(null);
    
    // Usa il RedirectService per un redirect più robusto
    this.redirectService.safeRedirectToLogin();
  }

  isLoggedIn(): boolean {
    const token = this.tokenStorage.getAccessToken();
    if (!token) return false;
    
    // Verifica se il token è scaduto
    try {
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))) as any;
      const exp = payload.exp * 1000; // Converti in millisecondi
      return Date.now() < exp;
    } catch {
      return false;
    }
  }

  isTokenExpired(): boolean {
    return !this.isLoggedIn();
  }

  // Metodo pubblico per forzare il redirect in caso di problemi
  forceLogout(): void {
    this.logout();
  }

  getAccessToken(): string | null {
    return this.tokenStorage.getAccessToken();
  }

  getRefreshToken(): string | null {
    return this.tokenStorage.getRefreshToken();
  }

  refreshAccessToken(): Observable<string> {
    if (this.isRefreshing) {
      // Se è già in corso un refresh, metti in coda la richiesta fino a quando abbiamo il nuovo token
      return this.refreshTokenSubject.pipe(
        filter((token): token is string => token !== null),
        take(1)
      );
    }
    
    const currentAccessToken = this.getAccessToken();
    const currentRefreshToken = this.getRefreshToken();
    const accountId = this.tokenStorage.getAccountId();

    if (!currentAccessToken || !currentRefreshToken || accountId === null) {
      return throwError(() => new Error('Missing tokens or account id'));
    }

    type RefreshResponse = {
      data: {
        jwt_token_id: number;
        account_id: number;
        account_first_name: string;
        account_last_name: string;
        jwt_token_access: string;
        jwt_token_refresh: string;
        jwt_token_creation: string;
        jwt_token_is_valid: boolean;
        jwt_refresh_token_expiration: string;
        account_role: string;
      };
      isSuccess: boolean;
      message: string;
    };

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return this.http
      .post<RefreshResponse>(`${environment.apiUrl}/auth/refresh-session`, {
        jwt_token: currentAccessToken,
        jwt_refresh_token: currentRefreshToken,
        account_id: accountId,
      })
      .pipe(
        map((response) => {
          if (!response.isSuccess) {
            throw new Error(response.message || 'Refresh token failed');
          }
          
          const newAccess = response.data.jwt_token_access;
          const newRefresh = response.data.jwt_token_refresh;
          this.tokenStorage.saveAccessToken(newAccess);
          this.tokenStorage.saveRefreshToken(newRefresh);
          
          // Salva i dati dell'account dal refresh
          if (response.data.account_id) {
            this.tokenStorage.saveAccountId(response.data.account_id);
          }
          if (response.data.account_role) {
            this.tokenStorage.saveRole(response.data.account_role);
          }
          if (response.data.account_first_name) {
            this.tokenStorage.saveAccountFirstName(response.data.account_first_name);
          }
          if (response.data.account_last_name) {
            this.tokenStorage.saveAccountLastName(response.data.account_last_name);
          }
          
          // Estrai e salva tutti i claims dal nuovo token
          const isAccountEnabled = this.tokenStorage.extractAndSaveClaimsFromToken();
          
          // Se l'account è disabilitato, fai logout
          if (!isAccountEnabled) {
            this.logout();
            throw new Error('Account is disabled');
          }
          
          this.refreshTokenSubject.next(newAccess);
          this.isRefreshing = false;
          return newAccess;
        }),
        catchError((err) => {
          this.refreshTokenSubject.next(null);
          this.isRefreshing = false;
          this.forceLogout();
          return throwError(() => err);
        })
      );
  }
}
